/*
 * DELIM blog article helpers, used by:
 *   blocks/delim_article_body.liquid   <delim-article-body>   heading ids + "#" links
 *   blocks/delim_article_toc.liquid    <delim-article-toc>    table of contents
 *   blocks/delim_article_share.liquid  <delim-article-share>  native share + copy link
 *
 * The logic is plain functions (exported for the unit tests in
 * tests/delim-article.test.js). The elements are only defined when this runs
 * in a browser. Each block loads this file, so it must be safe to run twice.
 */
(function () {
  var MAX_SLUG = 80;
  var COPIED_MS = 1800;
  var TOC_FALLBACK_MS = 600;
  var HEADINGS_READY = 'dab:headings-ready';

  /* "Why We Breathe?" -> "why-we-breathe". Only a-z, 0-9 and "-" survive,
     so accented letters are dropped, not converted. Capped at 80 chars. */
  function slugify(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, MAX_SLUG);
  }

  /* First free slug: base, base-2, base-3, ... isTaken(slug) says whether
     another heading or element already uses it. */
  function uniqueSlug(base, isTaken) {
    var slug = base;
    var i = 2;
    while (isTaken(slug)) slug = base + '-' + i++;
    return slug;
  }

  /* Ids for the article's h2/h3 headings, in order.
     headings: [{ id, text }]. A heading that already has an id keeps it.
     A heading whose text has no usable characters gets '' (no id, no link).
     idExists(id) says whether the page already has an element with that id.
     Returns a new array of ids; the input is not changed. */
  function headingIds(headings, idExists) {
    var seen = {};
    var exists = idExists || function () { return false; };
    return headings.map(function (h) {
      if (h.id) return h.id;
      var base = slugify(h.text || '');
      if (!base) return '';
      var slug = uniqueSlug(base, function (s) { return seen[s] || exists(s); });
      seen[slug] = true;
      return slug;
    });
  }

  /* CSS selector for the headings the TOC lists. */
  function tocSelector(includeH3) {
    return includeH3 ? 'h2, h3' : 'h2';
  }

  /* Heading text without the trailing "#" link the body block adds. */
  function tocLabel(text) {
    return (text || '').replace(/#$/, '').trim();
  }

  /* TOC rows from headings [{ id, tag, text }]. Headings without an id are
     skipped (nothing to link to). level is 3 for h3, else 2; index is the
     heading's position in the input. */
  function tocEntries(headings) {
    var out = [];
    headings.forEach(function (h, index) {
      if (!h.id) return;
      out.push({
        index: index,
        id: h.id,
        href: '#' + h.id,
        text: tocLabel(h.text),
        level: String(h.tag).toUpperCase() === 'H3' ? 3 : 2
      });
    });
    return out;
  }

  /* Copies text with the Clipboard API, falling back to the old
     execCommand('copy') way when it is missing or fails.
     env: { clipboard, fallbackCopy(text) } where fallbackCopy throws on
     failure. Resolves true when the text was copied, false otherwise. */
  function copyText(text, env) {
    var fallback = function () {
      try {
        env.fallbackCopy(text);
        return true;
      } catch {
        return false;
      }
    };
    var cb = env.clipboard;
    if (cb && cb.writeText) {
      return Promise.resolve()
        .then(function () { return cb.writeText(text); })
        .then(function () { return true; }, fallback);
    }
    return Promise.resolve(fallback());
  }

  function defineElement() {
    var byId = function (id) { return document.getElementById(id); };

    var DelimArticleBody = class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        var root = this.querySelector('[id^="dab-prose-"]');
        if (!root) return;
        var anchorClass = this.dataset.anchorClass;
        var list = Array.prototype.slice.call(root.querySelectorAll('h2, h3'));
        var ids = headingIds(
          list.map(function (h) { return { id: h.id, text: h.textContent }; }),
          function (id) { return !!byId(id); }
        );
        list.forEach(function (h, i) {
          if (!ids[i]) return;
          h.id = ids[i];
          if (!h.querySelector('.' + anchorClass)) {
            var a = document.createElement('a');
            a.className = anchorClass;
            a.href = '#' + h.id;
            a.setAttribute('aria-label', 'Link to this section');
            a.textContent = '#';
            h.appendChild(a);
          }
        });
        document.dispatchEvent(new CustomEvent(HEADINGS_READY, { detail: { rootId: root.id } }));
      }
    };

    var DelimArticleToc = class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        var includeH3 = this.dataset.includeH3 === 'true';
        var listEl = this.querySelector('ol');
        var emptyEl = this.querySelector('[data-toc-empty]');
        if (!listEl) return;

        var build = function () {
          var prose = document.querySelector('[id^="dab-prose-"]');
          if (!prose) return false;
          var headings = prose.querySelectorAll(tocSelector(includeH3));
          if (!headings.length) { if (emptyEl) emptyEl.hidden = false; return true; }
          listEl.innerHTML = '';
          var byHeading = Array.prototype.slice.call(headings);
          var entries = tocEntries(byHeading.map(function (h) {
            return { id: h.id, tag: h.tagName, text: h.textContent };
          }));
          var anchors = entries.map(function (e) {
            var h = byHeading[e.index];
            var li = document.createElement('li');
            var a = document.createElement('a');
            a.href = e.href;
            a.textContent = e.text;
            if (e.level === 3) a.classList.add('lvl-3');
            a.addEventListener('click', function () {
              try { history.replaceState(null, '', e.href); } catch {}
            });
            li.appendChild(a);
            listEl.appendChild(li);
            return { a: a, h: h };
          });
          if (!anchors.length) { if (emptyEl) emptyEl.hidden = false; return true; }

          if ('IntersectionObserver' in window) {
            var io = new IntersectionObserver(function (items) {
              items.forEach(function (it) {
                if (!it.isIntersecting) return;
                anchors.forEach(function (x) { x.a.classList.toggle('is-active', x.h === it.target); });
              });
            }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });
            anchors.forEach(function (x) { io.observe(x.h); });
          }
          return true;
        };

        if (!build()) {
          document.addEventListener(HEADINGS_READY, function () { build(); }, { once: true });
          setTimeout(build, TOC_FALLBACK_MS);
        }
      }
    };

    var DelimArticleShare = class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        var url = this.dataset.shareUrl;
        var title = this.dataset.shareTitle;

        var nativeBtn = this.querySelector('[data-share-native]');
        if (nativeBtn && navigator.share) {
          nativeBtn.hidden = false;
          nativeBtn.addEventListener('click', function () {
            navigator.share({ title: title, url: url }).catch(function () {});
          });
        }

        var copyBtn = this.querySelector('[data-share-copy]');
        if (!copyBtn) return;
        var fallbackCopy = function (text) {
          var ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          try {
            document.execCommand('copy');
          } finally {
            document.body.removeChild(ta);
          }
        };
        copyBtn.addEventListener('click', function () {
          copyText(url, { clipboard: navigator.clipboard, fallbackCopy: fallbackCopy }).then(function (ok) {
            if (!ok) return;
            copyBtn.classList.add('copied');
            setTimeout(function () { copyBtn.classList.remove('copied'); }, COPIED_MS);
          });
        });
      }
    };

    // Body first, so headings have ids before the TOC (placed above it) reads them.
    customElements.define('delim-article-body', DelimArticleBody);
    customElements.define('delim-article-toc', DelimArticleToc);
    customElements.define('delim-article-share', DelimArticleShare);
  }

  var api = {
    MAX_SLUG: MAX_SLUG,
    COPIED_MS: COPIED_MS,
    slugify: slugify,
    uniqueSlug: uniqueSlug,
    headingIds: headingIds,
    tocSelector: tocSelector,
    tocLabel: tocLabel,
    tocEntries: tocEntries,
    copyText: copyText
  };

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('delim-article-body')) {
    defineElement();
  }
})();
